import { useState } from 'react';
import { X, ShoppingBag, Plus, Minus, Image as ImageIcon, Maximize2, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useCart } from '@/contexts/CartContext';
import { fileUrl } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { formatWindowDate } from '@/lib/windowDate';
import { formatPack } from '@/lib/units';
import { money } from '@/lib/format';

function DetailInner({ product, onClose, showClose = true }) {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [lightbox, setLightbox] = useState(false);
  const img = fileUrl(product.image_url, 768);
  const imgFull = fileUrl(product.image_url);
  const upcoming = product.window_state === 'upcoming';
  const orderable = product.orderable !== false && product.window_open !== false && !upcoming;
  const opensOn = formatWindowDate(product.window_open_date);

  const add = () => {
    if (upcoming) {
      toast.info(opensOn ? `Opens on ${opensOn} — you can order then` : 'This vendor isn’t taking orders yet');
      return;
    }
    if (!orderable) {
      toast.error('Order window is closed');
      return;
    }
    const r = addItem(product, qty);
    if (r?.conflict) return;
    toast.success(`${qty}x ${product.name} added to cart`);
    setQty(1);
  };

  return (
    <div data-testid="product-detail-content">
      {/* Image — fits within the frame (object-contain), click to expand full size */}
      <div className="relative h-52 bg-gradient-to-br from-teal/20 to-charcoal flex items-center justify-center overflow-hidden">
        {img ? (
          <>
            <img
              src={img}
              alt={product.name}
              loading="lazy"
              decoding="async"
              onClick={() => setLightbox(true)}
              className="max-h-full max-w-full object-contain cursor-zoom-in"
              data-testid="product-detail-image"
            />
            <button
              onClick={() => setLightbox(true)}
              data-testid="product-detail-expand"
              aria-label="View full size image"
              className="absolute bottom-3 left-3 h-8 w-8 rounded-full bg-black/45 border border-white/15 text-cream flex items-center justify-center hover:bg-black/65 transition-colors"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </>
        ) : (
          <div className="text-center text-teal/70">
            <ImageIcon className="h-10 w-10 mx-auto opacity-50" strokeWidth={1.4} />
            <p className="font-body text-xs mt-2">No product image available</p>
          </div>
        )}
        {showClose && (
          <button
            onClick={onClose}
            data-testid="product-detail-close"
            aria-label="Close product details"
            className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/40 border border-border text-cream flex items-center justify-center hover:bg-black/60 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Full-size lightbox overlay */}
      {lightbox && img && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(false)}
          data-testid="product-image-lightbox"
        >
          <img
            src={imgFull}
            alt={product.name}
            decoding="async"
            className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightbox(false)}
            data-testid="product-image-lightbox-close"
            aria-label="Close full size image"
            className="absolute top-5 right-5 h-10 w-10 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className="p-5">
        {product.supplier_name && (
          <Badge
            variant="outline"
            className="rounded-full font-body text-xs border-gold/30 text-gold mb-3 max-w-full truncate"
            data-testid="product-detail-supplier"
          >
            {product.supplier_name}
          </Badge>
        )}
        <h3 className="font-heading text-2xl text-foreground leading-tight" data-testid="product-detail-name">
          {product.name}
        </h3>
        {formatPack(product) && (
          <p className="font-body text-sm font-semibold text-teal mt-1" data-testid="product-detail-size">
            {formatPack(product)}
          </p>
        )}
        <p className="font-heading text-2xl font-semibold text-gold mt-3">R{money(product.price)}</p>
        {upcoming && (
          <span className="inline-flex items-center gap-1.5 mt-3 bg-gold/15 text-gold border border-gold/40 rounded-full px-3 py-1 font-body text-xs font-bold" data-testid="product-detail-upcoming">
            <CalendarClock className="h-3.5 w-3.5" />
            {opensOn ? `Opening Soon · Opens ${opensOn}` : 'Opening Soon'}
          </span>
        )}

        {product.description ? (
          <>
            <p className="font-body text-[11px] uppercase tracking-[0.14em] text-teal/80 mt-5 mb-1.5">Description</p>
            <p className="font-body text-sm text-foreground/80 leading-relaxed" data-testid="product-detail-description">
              {product.description}
            </p>
          </>
        ) : (
          <p className="font-body text-sm text-foreground/40 italic mt-4">No description available for this product.</p>
        )}

        <div className="flex items-center gap-3 mt-6">
          {orderable && (
            <div className="flex items-center border border-border rounded-lg overflow-hidden">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="px-2.5 py-2 text-foreground/60 hover:text-foreground hover:bg-muted transition-colors" data-testid="product-detail-qty-minus">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-body text-sm text-foreground" data-testid="product-detail-qty">{qty}</span>
              <button onClick={() => setQty(q => q + 1)} className="px-2.5 py-2 text-foreground/60 hover:text-foreground hover:bg-muted transition-colors" data-testid="product-detail-qty-plus">
                <Plus className="h-4 w-4" />
              </button>
            </div>
          )}
          <Button
            onClick={add}
            disabled={!orderable}
            data-testid="product-detail-add"
            className={`flex-1 rounded-xl font-body text-sm font-semibold gap-2 ${
              orderable ? 'bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light'
                : upcoming ? 'bg-gold/20 text-gold border border-gold/40 cursor-not-allowed'
                : 'bg-muted text-foreground/50 cursor-not-allowed'
            }`}
          >
            {upcoming ? <CalendarClock className="h-4 w-4" /> : <ShoppingBag className="h-4 w-4" />}
            {upcoming ? (opensOn ? `Opens ${opensOn}` : 'Opening soon') : !orderable ? 'Orders Closed' : 'Add to Cart'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Centered modal overlay — used for Shop "Quick View". */
export function ProductDetailModal({ product, onClose }) {
  return (
    <Dialog open={!!product} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="p-0 gap-0 max-w-md overflow-hidden border-border bg-background [&>button]:z-50 [&>button]:bg-black/40 [&>button]:text-cream [&>button]:hover:bg-black/60 [&>button]:hover:text-cream"
        data-testid="product-detail-modal"
      >
        {product && (
          <>
            <DialogTitle className="sr-only">{product.name}</DialogTitle>
            <DialogDescription className="sr-only">Product details for {product.name}</DialogDescription>
            <div className="max-h-[85vh] overflow-y-auto">
              <DetailInner product={product} onClose={onClose} showClose={false} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
